import { useEffect, useRef } from "react";
import { SyntaxHighlighter } from "../lib/syntax-highlighter";
import oneDark from "react-syntax-highlighter/dist/esm/styles/prism/one-dark";

interface CodeExamplePanelProps {
  code: string;
  language: string;
  title: string;
  isStreaming: boolean;
}

export default function CodeExamplePanel({
  code,
  language,
  title,
  isStreaming,
}: CodeExamplePanelProps) {
  const codeContainerRef = useRef<HTMLDivElement>(null);
  const wasStreamingRef = useRef(false);

  // Auto-scroll to bottom while streaming, scroll to top when done
  useEffect(() => {
    if (isStreaming && codeContainerRef.current) {
      codeContainerRef.current.scrollTop =
        codeContainerRef.current.scrollHeight;
      wasStreamingRef.current = true;
    } else if (!isStreaming && wasStreamingRef.current && codeContainerRef.current) {
      codeContainerRef.current.scrollTop = 0;
      wasStreamingRef.current = false;
    }
  }, [code, isStreaming]);

  if (!code) return null;

  return (
    <div className="ghw-flex ghw-flex-col ghw-rounded-lg ghw-border ghw-border-border ghw-overflow-hidden ghw-h-full">
      {/* Header */}
      <div className="ghw-flex ghw-items-center ghw-justify-between ghw-px-4 ghw-py-2 ghw-bg-replay-surface ghw-border-b ghw-border-border ghw-shrink-0">
        <span className="ghw-text-xs ghw-font-medium ghw-text-muted-foreground ghw-truncate">
          {title}
        </span>
        <span className="ghw-text-[10px] ghw-font-medium ghw-uppercase ghw-text-muted-foreground ghw-px-2 ghw-py-0.5 ghw-rounded ghw-bg-muted ghw-shrink-0 ghw-ml-2">
          {language}
        </span>
      </div>

      {/* Code block with syntax highlighting */}
      <div
        ref={codeContainerRef}
        className="ghw-overflow-auto ghw-flex-1 ghw-min-h-0 ghw-bg-replay-code-bg"
      >
        <SyntaxHighlighter
          language={language === "text" ? undefined : language}
          style={oneDark}
          customStyle={{
            margin: 0,
            padding: "1rem",
            background: "transparent",
            fontSize: "12px",
          }}
          codeTagProps={{
            className: "ghw-text-[11px] sm:ghw-text-xs",
          }}
          wrapLongLines
        >
          {code}
        </SyntaxHighlighter>
      </div>
    </div>
  );
}
